// 로그인 게이트 — PBKDF2 검증, 락아웃, 세션 생성, 감사 로그, 무활동 자동 로그아웃 (정책값은 서버 시스템 설정 반영)
import { useEffect, useRef, useState, type PropsWithChildren } from 'react';
import {
  AUTH_CONFIG,
  audit,
  clearSession,
  createSession,
  getAuthPolicy,
  isLocked,
  readSession,
  recordFailure,
  refreshSession,
  syncAuthPolicyFromServer,
  verifyPassword,
  clearLockout,
} from '../lib/auth';
import { useUI } from '../lib/uiStore';

export function LoginGate({ children }: PropsWithChildren) {
  const authVersion = useUI((s) => s.authVersion);
  const [session, setSession] = useState(() => readSession());
  const [policy, setPolicy] = useState(() => getAuthPolicy());

  // 마운트 시 + 로그인 화면으로 돌아올 때(session=null) 정책 재읽기 — 시스템 설정 탭에서 저장한 값이
  // 새로고침 없이 다음 로그인 화면부터 반영. 서버 동기화(락아웃 횟수/시간·무활동 시간) 실패 시 기존 값 유지
  useEffect(() => {
    let alive = true;
    setPolicy(getAuthPolicy());
    void syncAuthPolicyFromServer().then((p) => { if (alive && p) setPolicy(p); });
    return () => { alive = false; };
  }, [session]);

  // App.tsx의 로그아웃이 authVersion을 올리면 여기서 감지해 로그인 화면으로 전환
  useEffect(() => {
    const s = readSession();
    if (!s && session) setSession(null);
  }, [authVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lock, setLock] = useState(() => isLocked());
  const [busy, setBusy] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 무활동 타이머 — 마우스/키보드 이벤트 발생 시 갱신
  useEffect(() => {
    if (!session) return;
    function scheduleLogout() {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        audit('session.expired', `user=${session?.username}`);
        clearSession();
        setSession(null);
        setError('무활동 시간이 길어 자동 로그아웃되었습니다.');
      }, policy.idleTimeoutMs);
    }
    function onActivity() {
      const s = refreshSession();
      if (!s) {
        setSession(null);
        return;
      }
      scheduleLogout();
    }
    scheduleLogout();
    const events = ['mousedown', 'keydown', 'pointermove', 'scroll', 'touchstart'] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [session, policy.idleTimeoutMs]);

  // 락아웃 카운트다운 표시 (1초 단위)
  useEffect(() => {
    if (!lock.locked) return;
    const id = setInterval(() => {
      const cur = isLocked();
      setLock(cur);
      if (!cur.locked) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [lock.locked]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const cur = isLocked();
    if (cur.locked) {
      setLock(cur);
      setError(`너무 많은 시도. 잠시 후 다시 시도하세요. (남은 시간 ${Math.ceil(cur.remainMs / 1000)}초)`);
      return;
    }
    if (!username || !password) return;
    setBusy(true);
    try {
      const ok = await verifyPassword(username, password);
      if (ok) {
        const sess = createSession(username);
        clearLockout();
        audit('login.success', `user=${username}`);
        setSession(sess);
        setUsername('');
        setPassword('');
      } else {
        // PII 보호: 비밀번호는 감사 로그에 절대 기록 안 함
        audit('login.failure', `user=${username.slice(0, 32)}`);
        const next = recordFailure();
        if (next.lockedUntil) {
          audit('login.lockout', `user=${username.slice(0, 32)} until=${next.lockedUntil}`);
          setLock({ locked: true, remainMs: policy.lockoutMs });
          setError(`너무 많은 시도. 잠시 후 다시 시도하세요. (${policy.lockoutMs / 1000}초)`);
        } else {
          setError(`인증 실패 (${next.attempts}/${policy.maxAttempts})`);
        }
      }
    } finally {
      setBusy(false);
      setPassword('');
    }
  }

  if (session) {
    return (
      <>
        <SessionContext value={session.username} onLogout={() => {
          audit('logout', `user=${session.username}`);
          clearSession();
          setSession(null);
        }} />
        {children}
      </>
    );
  }

  const remainSec = Math.ceil(lock.remainMs / 1000);
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg)',
      }}
    >
      <form
        onSubmit={onSubmit}
        role="form"
        aria-label="로그인"
        autoComplete="off"
        style={{
          width: 420,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: 32,
        }}
      >
        <div className="brand-mark" style={{ marginBottom: 22 }}>
          <div className="brand-icon">🛡</div>
          <div>
            <div className="brand-text">File<em>Guard</em> 관리자</div>
            <div className="brand-tag">SIGN IN</div>
          </div>
        </div>

        <div className="form-row">
          <label className="form-label" htmlFor="login-user">사용자 ID</label>
          <input
            id="login-user"
            className="form-input mono"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            disabled={lock.locked || busy}
            maxLength={64}
            required
          />
        </div>
        <div className="form-row">
          <label className="form-label" htmlFor="login-pass">비밀번호</label>
          <input
            id="login-pass"
            className="form-input mono"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            disabled={lock.locked || busy}
            maxLength={128}
            required
          />
        </div>

        {error && (
          <div
            className="reason-box reason-danger"
            role="alert"
            style={{ marginTop: 0 }}
          >
            ⛔ <div>{error}</div>
          </div>
        )}
        {lock.locked && (
          <div className="reason-box reason-warn" role="alert" style={{ marginTop: 8 }}>
            ⚠ <div>로그인 시도 제한 중 — {remainSec}초 후 재시도 가능</div>
          </div>
        )}

        <button
          type="submit"
          className="btn btn-pri"
          disabled={busy || lock.locked || !username || !password}
          style={{
            width: '100%',
            marginTop: 16,
            justifyContent: 'center',
            opacity: (busy || lock.locked || !username || !password) ? 0.4 : 1,
          }}
        >
          {busy ? '인증 중...' : '🔐 로그인'}
        </button>

        <div className="text-mono text-muted" style={{ marginTop: 16, fontSize: 12, lineHeight: 1.7 }}>
          ※ 본 시스템은 OWASP / 국정원 / 행안부 보안 가이드에 따라 PBKDF2(SHA-256, {AUTH_CONFIG.iterations.toLocaleString()} iter) 해시 검증을 사용합니다.<br />
          ※ {policy.maxAttempts}회 연속 실패 시 {policy.lockoutMs / 60000}분 락아웃. {policy.idleTimeoutMs / 60000}분 무활동 시 자동 로그아웃.
        </div>
      </form>
    </div>
  );
}

function SessionContext({ value: _v, onLogout: _o }: { value: string; onLogout: () => void }) {
  // 자식이 useSession을 통해 username + logout 트리거를 얻도록 context를 사용할 수도 있으나,
  // 여기서는 App 내부 단순 토픽으로 충분하므로 노출만 한다.
  return null;
}
